import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Newspaper, RefreshCw, ExternalLink, Flame } from 'lucide-react';

const CATEGORY_ICON = {
  india_markets: '🇮🇳',
  us_markets: '🇺🇸',
  asian_markets: '🌏',
  gold: '🥇',
  forex: '💱',
  commodities: '🛢️',
  world: '🌍',
  business: '📈',
};

const timeAgo = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return '';
  }
};

const WorldNewsCard = () => {
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState('india_markets');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/news/categories`).then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  const load = async (cat) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/news/feed`, { params: { category: cat }, timeout: 15000 });
      setData(r.data);
    } catch {
      setData({ articles: [], label: cat });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(active); /* eslint-disable-next-line */ }, [active]);

  return (
    <section className="world-news" data-testid="world-news">
      <div className="wn-wrap">
        <div className="wn-head">
          <div className="wn-title-row">
            <div className="wn-title">
              <span className="wn-icon"><Newspaper size={18} /></span>
              <div>
                <div className="wn-eyebrow"><Flame size={12} /> LIVE FINANCE HEADLINES</div>
                <h3>Markets, Gold, Forex &amp; World Finance</h3>
              </div>
            </div>
            <button onClick={() => load(active)} className="wn-refresh" data-testid="wn-refresh" title="Refresh">
              <RefreshCw size={14} className={loading ? 'spin' : ''} />
            </button>
          </div>

          <div className="wn-tabs" role="tablist">
            {categories.map((c) => (
              <button
                key={c.key}
                role="tab"
                aria-selected={active === c.key}
                onClick={() => setActive(c.key)}
                className={`wn-tab ${active === c.key ? 'active' : ''}`}
                data-testid={`wn-tab-${c.key}`}
              >
                <span className="wn-tab-emoji">{CATEGORY_ICON[c.key] || '📰'}</span>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="wn-grid" data-testid="wn-articles-grid">
          {loading && !data && (
            <div className="wn-empty">Loading headlines…</div>
          )}
          {data && data.articles.length === 0 && (
            <div className="wn-empty">No headlines available right now. Try another category.</div>
          )}
          {data && data.articles.map((a, i) => (
            <a
              key={`${active}-${i}-${(a.link || '').slice(-32)}`}
              href={a.link}
              target="_blank"
              rel="noopener noreferrer"
              className="wn-card"
              data-testid={`wn-article-${i}`}
            >
              <div className="wn-card-source">
                <span>{a.source || 'News'}</span>
                <span className="wn-dot">·</span>
                <span>{timeAgo(a.published)}</span>
              </div>
              <div className="wn-card-title">{a.title}</div>
              <div className="wn-card-cta">
                Read full <ExternalLink size={12} />
              </div>
            </a>
          ))}
        </div>
      </div>

      <style>{`
        .world-news { padding: 4rem 2rem; background: linear-gradient(180deg, #0F172A 0%, #1E293B 100%); }
        .wn-wrap { max-width: 1300px; margin: 0 auto; }
        .wn-head { margin-bottom: 2rem; }
        .wn-title-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.25rem; }
        .wn-title { display: flex; align-items: center; gap: 14px; }
        .wn-icon { width: 42px; height: 42px; border-radius: 12px; background: linear-gradient(135deg, #FF6B35 0%, #F97316 100%); display: flex; align-items: center; justify-content: center; color: white; box-shadow: 0 8px 20px rgba(255,107,53,0.35); }
        .wn-title h3 { color: white; font-family: 'Outfit', sans-serif; font-size: 1.5rem; font-weight: 800; margin: 0; }
        .wn-eyebrow { display: inline-flex; align-items: center; gap: 6px; color: #FDBA74; font-size: 0.7rem; font-weight: 800; letter-spacing: 0.12em; }
        .wn-refresh { width: 38px; height: 38px; border-radius: 50%; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); color: white; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .wn-refresh:hover { background: rgba(255,107,53,0.18); border-color: #FF6B35; }
        .wn-tabs { display: flex; flex-wrap: wrap; gap: 8px; overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .wn-tab { display: inline-flex; align-items: center; gap: 6px; padding: 9px 16px; border-radius: 999px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: #CBD5E1; font-size: 0.85rem; font-weight: 600; cursor: pointer; white-space: nowrap; transition: all .2s ease; }
        .wn-tab:hover { background: rgba(255,255,255,0.08); color: white; }
        .wn-tab.active { background: linear-gradient(135deg, #FF6B35 0%, #F97316 100%); color: white; border-color: transparent; box-shadow: 0 4px 14px rgba(255,107,53,0.45); }
        .wn-tab-emoji { font-size: 1rem; }
        .wn-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1rem; }
        .wn-card { display: flex; flex-direction: column; gap: 8px; padding: 1.1rem 1.2rem; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 14px; text-decoration: none; color: inherit; transition: all .2s ease; }
        .wn-card:hover { background: rgba(255,255,255,0.07); border-color: #FF6B35; transform: translateY(-2px); }
        .wn-card-source { display: flex; align-items: center; gap: 6px; font-size: 0.7rem; font-weight: 600; color: #FDBA74; text-transform: uppercase; letter-spacing: 0.06em; }
        .wn-dot { opacity: 0.5; }
        .wn-card-title { color: white; font-weight: 600; font-size: 0.95rem; line-height: 1.45; flex: 1; }
        .wn-card-cta { display: inline-flex; align-items: center; gap: 4px; color: #FF6B35; font-size: 0.78rem; font-weight: 700; margin-top: 6px; }
        .wn-empty { color: #94A3B8; padding: 2rem; text-align: center; grid-column: 1 / -1; }
        .spin { animation: wn-spin 1s linear infinite; }
        @keyframes wn-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
    </section>
  );
};

export default WorldNewsCard;
